import * as React from 'react';
import { Action, Dispatch } from 'redux';
import { AppState, history } from 'src';
import {
  ServiceWorkOrderSearchViewModel,
  ServiceWorkOrderViewModel,
  ContractCostHistoryViewModel,
  ContractCviViewModel,
  ContractExclusionViewModel,
  ContractTotalCostHistoryViewModel,
  TripStatusLogViewModel,
  CVIExclusionViewModel
} from '@models/ServiceWorkOrder';
import { toast } from 'react-toastify';
import { ToastComponent, ToastType, ToastOptions } from '@common/components';
import { swoService } from '@services/ServiceWorkOrderService';
import { PageViewModel, OptionsViewModel, AppDispatcher } from '@models/common.models';
import { SwoPaymentViewModel } from '@models/ServiceWorkOrder';
import * as moment from 'moment-timezone';
import { dateParser } from '@common/dateParser';
import { CustomerViewModel } from '@models/Customer';
import { customerService } from '@services/CustomerService';
import { actions } from 'react-redux-form';
import { contractService } from '@services/ContractService';
import { SwoAppointmentViewModel } from '@models/SwoAppointment';
import { InvoiceViewModel } from '@models/Invoice';
import { getVendorDetails } from '../vendor/vendor.actions';
import { PropertyManagerViewModel } from '@models/PropertyManager';
import { ServiceWorkOrderTypes } from '@constants/ServiceWorkOrderTypes';
import { getRoute, RouteDefinitions } from '@common/route-definitions';

export enum ServiceWorkOrderActionType {
  /********** SaveServiceWorkOrder **********/
  SaveServiceWorkOrderStart = 'SaveServiceWorkOrderStart',
  SaveServiceWorkOrderSuccess = 'SaveServiceWorkOrderSuccess',
  SaveServiceWorkOrderFailure = 'SaveServiceWorkOrderFailure',

  /**** Get Appointments *****/
  GetSwoAppointmentsStart = 'GetSwoAppointmentsStart',
  GetSwoAppointmentsSuccess = 'GetSwoAppointmentsSuccess',
  GetSwoAppointmentsFailure = 'GetSwoAppointmentsFailure',

  /**** Get Invoices *****/
  GetSwoInvoicesStart = 'GetSwoInvoicesStart',
  GetSwoInvoicesSuccess = 'GetSwoInvoicesSuccess',
  GetSwoInvoicesFailure = 'GetSwoInvoicesFailure',

  /**** Get SWO *****/
  GetSWOStart = 'GetSWOStart',
  GetSWOSuccess = 'GetSWOSuccess',
  GetSWOFailure = 'GetSWOFailure',

  /*** Search SWO ***/
  SearchServiceWorkOrderStart = 'SearchServiceWorkOrderStart',
  SearchServiceWorkOrderSuccess = 'SearchServiceWorkOrderSuccess',
  SearchServiceWorkOrderFailure = 'SearchServiceWorkOrderFailure',
  ResetSWOSearch = 'ResetSWOSearch',

  /********** GetServiceWorkOrderContract **********/
  GetServiceWorkOrderContractStart = 'GetServiceWorkOrderContractStart',
  GetServiceWorkOrderContractSuccess = 'GetServiceWorkOrderContractSuccess',
  GetServiceWorkOrderContractFailure = 'GetServiceWorkOrderContractFailure',
  LoadPropertyManager = 'LoadPropertyManager',
  ClearPropertyManager = 'ClearPropertyManager',

  /********** Save Service Fee Payment *****/
  SaveServiceFeePaymentInfoStart = 'SaveServiceFeePaymentInfoStart',
  SaveServiceFeePaymentInfoSuccess = 'SaveServiceFeePaymentInfoSuccess',
  SaveServiceFeePaymentInfoFailure = 'SaveServiceFeePaymentInfoFailure',

  /**** Step 4: Get/Add/Remove Inventory ****/
  GetInventoryListStart = 'GetInventoryListStart',
  GetInventoryListSuccess = 'GetInventoryListSuccess',
  GetInventoryListFailure = 'GetInventoryListFailure',

  AddNewInventory = 'AddNewInventory',

  /********** Select CVI ***********/
  SelectCVI = 'SelectCVI',
  AddSelectedCVI = 'AddSelectedCVI',

  /********** GetServiceWorkOrderContractCVI **********/
  GetServiceWorkOrderContractCVIStart = 'GetServiceWorkOrderContractCVIStart',
  GetServiceWorkOrderContractCVISuccess = 'GetServiceWorkOrderContractCVISuccess',
  GetServiceWorkOrderContractCVIFailure = 'GetServiceWorkOrderContractCVIFailure',

  AddNewExclusionStart = 'AddNewExclusionStart',
  AddNewExclusionSuccess = 'AddNewExclusionSuccess',
  AddNewExclusionFailure = 'AddNewExclusionFailure',

  GetExclusionListStart = 'GetExclusionsStart',
  GetExclusionListSuccess = 'GetExclusionsSuccess',
  GetExclusionListFailure = 'GetExclusionsFailure',
  GetExclusionTypesStart = 'GetExclusionTypesStart',
  GetExclusionTypesSuccess = 'GetExclusionTypesSuccess',
  GetExclusionTypesFailure = 'GetExclusionTypesFailure',

  /********** SaveServiceWorkOrderCreate **********/
  SaveServiceWorkOrderCreate = 'SaveServiceWorkOrderCreate',

  SaveNotes = 'SaveNotes',

  /***** Contract Cost History *****/
  GetContractCostHistoryStart = 'GetContractCostHistoryStart',
  GetContractCostHistorySuccess = 'GetContractCostHistorySuccess',
  GetContractCostHistoryFailure = 'GetContractCostHistoryFailure',

  /**** Edit Exclusion ****/
  EditExclusionStart = 'EditExclusionStart',
  EditExclusionSuccess = 'EditExclusionSuccess',
  EditExclusionFailure = 'EditExclusionFailure',

  /*** Load Swo for Edit ***/
  LoadSWO = 'LoadSWO',

  /**** Assign Vendor to SWO ***/
  AssignVendorStart = 'AssignVendorStart',
  AssignVendorSuccess = 'AssignVendorSuccess',
  AssignVendorFailure = 'AssignVendorFailure',
  ResetSaveAndClose = 'ResetSaveAndClose',
  StoreVendor = 'StoreVendor',

  /**** Redispatch SWO ***/
  RedispatchSWOFailure = 'RedispatchSWOFailure',
  RedispatchSWOStart = 'RedispatchSWOStart',
  RedispatchSWOSuccess = 'RedispatchSWOSuccess',

  /**** GetTripStatusLog ****/
  GetTripStatusLogStart = 'GetTripStatusLogStart',
  GetTripStatusLogSuccess = 'GetTripStatusLogSuccess',
  GetTripStatusLogFailure = 'GetTripStatusLogFailure',

  ClearSwoDetails = 'ClearSwoDetails',

  ResetServiceWorkOrder = 'ResetServiceWorkOrder',

  SetEditPM = 'SetEditPM',

  SetTypes = 'SetTypes',
  SetTypeId = 'SetTypeId',
  SetSecondOpinionSWO = 'SetSecondOpinionSWO',
  /******* Zendesk Ticket Linking **********/
  SetSwoTicketId = 'SetSwoTicketId'
}

// This is defining the action definition for certain actions on the page.
// There may be multiple types of these on a given page.
// All properties should be readonly.
interface ServiceWorkOrderAction extends Action {
  readonly type: ServiceWorkOrderActionType;
}

/**** Get Swo Appointments *****/
export interface GetSwoAppointmentsStartAction extends ServiceWorkOrderAction {}
export interface GetSwoAppointmentsSuccessAction extends ServiceWorkOrderAction {
  swoAppointments: Array<SwoAppointmentViewModel>;
}

export interface GetSwoAppointmentsFailureAction extends ServiceWorkOrderAction {
  errorMessage: string;
}

/**** Get Swo Invoices *****/
export interface GetSwoInvoicesStartAction extends ServiceWorkOrderAction {}
export interface GetSwoInvoicesSuccessAction extends ServiceWorkOrderAction {
  swoInvoices: Array<InvoiceViewModel>;
}
export interface GetSwoInvoicesFailureAction extends ServiceWorkOrderAction {
  errorMessage: string;
}

/***** GETSWO */
export interface GetSWOStartAction extends ServiceWorkOrderAction {}
export interface GetSWOSuccessAction extends ServiceWorkOrderAction {
  readonly swoVM: ServiceWorkOrderViewModel;
}
export interface GetSWOFailureAction extends ServiceWorkOrderAction {
  readonly errorMessage: string;
}

/********** SaveServiceWorkOrder **********/
export interface SaveServiceWorkOrderStartAction extends ServiceWorkOrderAction {}
export interface SaveServiceWorkOrderSuccessAction extends ServiceWorkOrderAction {
  readonly id: number;
  readonly swoVM: ServiceWorkOrderViewModel;
}
export interface SaveServiceWorkOrderFailureAction extends ServiceWorkOrderAction {
  readonly errorMessage: string;
}

/************* Search SWO *************/
export interface SearchServiceWorkOrderStartAction extends ServiceWorkOrderAction {
  readonly query: string | null;
  readonly showInactive: boolean | undefined;
}
export interface SearchServiceWorkOrderSuccessAction extends ServiceWorkOrderAction {
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly orderBy: string;
  readonly result: PageViewModel<ServiceWorkOrderSearchViewModel>;
}
export interface SearchServiceWorkOrderFailureAction extends ServiceWorkOrderAction {
  readonly error: string;
}

export interface ResetSWOSearchAction extends ServiceWorkOrderAction {}

/********** GetServiceWorkOrderContract **********/
export interface GetServiceWorkOrderContractStartAction extends ServiceWorkOrderAction {
  readonly message: string;
}
export interface GetServiceWorkOrderContractSuccessAction extends ServiceWorkOrderAction {
  readonly message: string;
  readonly swoContract: ServiceWorkOrderViewModel;
  readonly customer?: CustomerViewModel;
}
export interface GetServiceWorkOrderContractFailureAction extends ServiceWorkOrderAction {
  readonly errorMessage: string;
}

export interface LoadPropertyManagerAction extends ServiceWorkOrderAction {
  readonly propertyManager: PropertyManagerViewModel;
}

export interface ClearPropertyManagerAction extends ServiceWorkOrderAction {}

/***** Step 3: Service Fee Payment ******/
export interface SaveServiceFeePaymentInfoStartAction extends ServiceWorkOrderAction {
  readonly message: string;
}
export interface SaveServiceFeePaymentInfoSuccessAction extends ServiceWorkOrderAction {
  readonly firstBilling?: OptionsViewModel;
  readonly laterBills?: OptionsViewModel;
  readonly message: string;
  readonly serviceWorkOrderId: number;
}
export interface SaveServiceFeePaymentInfoFailureAction extends ServiceWorkOrderAction {
  readonly errorMessage: string;
}

/**** Step 4: Get/Add/Remove New Inventory *****/
export interface GetInventoryListStartAction extends ServiceWorkOrderAction {}
export interface GetInventoryListSuccessAction extends ServiceWorkOrderAction {
  readonly inventoryList: Array<ContractCviViewModel>;
}
export interface GetInventoryListFailureAction extends ServiceWorkOrderAction {
  readonly message: string;
}

export interface GetExclusionListStartAction extends ServiceWorkOrderAction {}
export interface GetExclusionListSuccessAction extends ServiceWorkOrderAction {
  readonly exclusionList: Array<ContractExclusionViewModel>;
}
export interface GetExclusionListFailureAction extends ServiceWorkOrderAction {
  readonly message: string;
}

export interface AddNewInventoryAction extends ServiceWorkOrderAction {
  readonly inventoryEntry: ContractCviViewModel;
}

export interface AddNewExclusionStartAction extends ServiceWorkOrderAction {}
export interface AddNewExclusionSuccessAction extends ServiceWorkOrderAction {
  readonly exclusionEntry: CVIExclusionViewModel;
}
export interface AddNewExclusionFailureAction extends ServiceWorkOrderAction {
  readonly message: string;
}

export interface GetExclusionTypesStartAction extends ServiceWorkOrderAction {}
export interface GetExclusionTypesSuccessAction extends ServiceWorkOrderAction {
  readonly exclusionTypes: Array<OptionsViewModel>;
}
export interface GetExclusionTypesFailureAction extends ServiceWorkOrderAction {
  readonly message: string;
}

/********** Select CVI ***********/
export interface SelectCVIAction extends ServiceWorkOrderAction {
  readonly cvi: ContractCviViewModel;
}

export interface AddSelectedCVIAction extends ServiceWorkOrderAction {
  readonly cvi: number;
}

/********** GetServiceWorkOrderContractCVI **********/
export interface GetServiceWorkOrderContractCVIStartAction extends ServiceWorkOrderAction {
  readonly message: string;
}
export interface GetServiceWorkOrderContractCVISuccessAction extends ServiceWorkOrderAction {
  readonly message: string;
  readonly swoContractCVIs: ContractCviViewModel[];
}
export interface GetServiceWorkOrderContractCVIFailureAction extends ServiceWorkOrderAction {
  readonly errorMessage: string;
}

/********** SaveServiceWorkOrderCreate **********/
export interface SaveServiceWorkOrderCreateAction extends ServiceWorkOrderAction {
  readonly swo: ServiceWorkOrderViewModel;
}

export interface SaveNotesAction extends ServiceWorkOrderAction {
  readonly internalNotes: string;
  readonly vendorNotes: string;
}

/******* Contract Cost History *******/
export interface GetContractCostHistoryStartAction extends ServiceWorkOrderAction {}
export interface GetContractCostHistorySuccessAction extends ServiceWorkOrderAction {
  readonly costHistory: ContractTotalCostHistoryViewModel[];
}
export interface GetContractCostHistoryFailureAction extends ServiceWorkOrderAction {
  readonly message: string;
}

/**** Edit Exclusion ****/
export interface EditExclusionStartAction extends ServiceWorkOrderAction {}
export interface EditExclusionSuccessAction extends ServiceWorkOrderAction {}
export interface EditExclusionFailureAction extends ServiceWorkOrderAction {
  readonly message: string;
}

/**** Load SWO For Edit ****/
export interface LoadSWOAction extends ServiceWorkOrderAction {
  readonly swo: ServiceWorkOrderViewModel;
}

export interface SetEditPMAction extends Action {
  readonly type: ServiceWorkOrderActionType.SetEditPM;
  readonly editPM: boolean;
}

/**** Resend Vendor Email ****/
export interface ResendEmailSuccessAction extends ServiceWorkOrderAction {
  readonly message: string;
}
export interface ResendEmailFailureAction extends ServiceWorkOrderAction {
  readonly message: string;
}
/**** Assign Vendor to SWO ****/
export interface AssignVendorStartAction extends ServiceWorkOrderAction {}
export interface AssignVendorSuccessAction extends ServiceWorkOrderAction {
  readonly message: string;
}
export interface AssignVendorFailureAction extends ServiceWorkOrderAction {
  readonly message: string;
}
export interface StoreVendorAction extends ServiceWorkOrderAction {
  readonly vendorId: number;
}
export interface ResetSaveAndCloseAction extends ServiceWorkOrderAction {}

/***** Redispatch SWO *****/
export interface RedispatchSWOFailureAction extends ServiceWorkOrderAction {}
export interface RedispatchSWOStartAction extends ServiceWorkOrderAction {}
export interface RedispatchSWOSuccessAction extends ServiceWorkOrderAction {}

/*** GetTripStatusLog ***/
export interface GetTripStatusLogStartAction extends ServiceWorkOrderAction {}
export interface GetTripStatusLogSuccessAction extends ServiceWorkOrderAction {
  readonly statusLog: TripStatusLogViewModel[];
}
export interface GetTripStatusLogFailureAction extends ServiceWorkOrderAction {
  readonly message: string;
}

/**** Clear Swo Details ****/
export interface ClearSwoDetailsAction extends ServiceWorkOrderAction {
  readonly type: ServiceWorkOrderActionType.ClearSwoDetails;
}

export interface SetSwoTicketIdAction extends ServiceWorkOrderAction {
  readonly type: ServiceWorkOrderActionType.SetSwoTicketId;
  readonly id?: number;
}

export function setSwoTicketId(id?: number): SetSwoTicketIdAction {
  return {
    type: ServiceWorkOrderActionType.SetSwoTicketId,
    id: id
  };
}

export function clearSwoDetails(): ClearSwoDetailsAction {
  return {
    type: ServiceWorkOrderActionType.ClearSwoDetails
  };
}
/**** Get Service Work Order ******/
export function getSwo(id: number) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getSwoStart());
    try {
      const swo = await swoService.getSwo(id);
      return dispatch(getSwoSuccess(swo));
    } catch (err) {
      return dispatch(getSwoFailure(err));
    }
  };
}

function setEditPM(editPM: boolean): SetEditPMAction {
  return {
    type: ServiceWorkOrderActionType.SetEditPM,
    editPM: editPM
  };
}
export function toggleIsEditing(editPM: boolean) {
  return (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    dispatch(setEditPM(editPM));
  };
}

export function loadPropertyManager(propertyManager: PropertyManagerViewModel): LoadPropertyManagerAction {
  return {
    type: ServiceWorkOrderActionType.LoadPropertyManager,
    propertyManager: propertyManager
  };
}

export function clearPropertyManager(): ClearPropertyManagerAction {
  return {
    type: ServiceWorkOrderActionType.ClearPropertyManager
  };
}

export function getSwoStart(): GetSWOStartAction {
  return {
    type: ServiceWorkOrderActionType.GetSWOStart
  };
}
export function getSwoSuccess(swo: ServiceWorkOrderViewModel): GetSWOSuccessAction {
  return {
    type: ServiceWorkOrderActionType.GetSWOSuccess,
    swoVM: swo
  };
}

export function getSwoFailure(errorMessage: string): GetSWOFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Unable to get Service Work Order. Error: ' + errorMessage} />,
    ToastOptions.Error
  );

  return {
    type: ServiceWorkOrderActionType.GetSWOFailure,
    errorMessage: errorMessage
  };
}

/**** Get Swo Appointments ******/
export function getSwoAppointments(id: number) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getSwoAppointmentsStart());
    try {
      const appts = await swoService.getSwoAppointments(id);
      return dispatch(getSwoAppointmentsSuccess(appts));
    } catch (err) {
      return dispatch(getSwoAppointmentsFailure(err));
    }
  };
}

export function getSwoAppointmentsStart(): GetSwoAppointmentsStartAction {
  return {
    type: ServiceWorkOrderActionType.GetSwoAppointmentsStart
  };
}
export function getSwoAppointmentsSuccess(
  appointments: Array<SwoAppointmentViewModel>
): GetSwoAppointmentsSuccessAction {
  return {
    type: ServiceWorkOrderActionType.GetSwoAppointmentsSuccess,
    swoAppointments: appointments
  };
}

export function getSwoAppointmentsFailure(errorMessage: string): GetSwoAppointmentsFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Unable to get Swo Appointments. Error: ' + errorMessage} />,
    ToastOptions.Error
  );

  return {
    type: ServiceWorkOrderActionType.GetSwoAppointmentsFailure,
    errorMessage: errorMessage
  };
}

/**** Get Swo Invoices ******/
export function getSwoInvoices(id: number) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getSwoInvoicesStart());
    try {
      const appts = await swoService.getSwoInvoices(id);
      return dispatch(getSwoInvoicesSuccess(appts));
    } catch (err) {
      return dispatch(getSwoInvoicesFailure(err));
    }
  };
}

export function getSwoInvoicesStart(): GetSwoInvoicesStartAction {
  return {
    type: ServiceWorkOrderActionType.GetSwoInvoicesStart
  };
}
export function getSwoInvoicesSuccess(invoices: Array<InvoiceViewModel>): GetSwoInvoicesSuccessAction {
  return {
    type: ServiceWorkOrderActionType.GetSwoInvoicesSuccess,
    swoInvoices: invoices
  };
}

export function getSwoInvoicesFailure(errorMessage: string): GetSwoInvoicesFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Unable to get Swo Invoices. Error: ' + errorMessage} />,
    ToastOptions.Error
  );

  return {
    type: ServiceWorkOrderActionType.GetSwoInvoicesFailure,
    errorMessage: errorMessage
  };
}

/********** SaveServiceWorkOrder **********/
export function saveServiceWorkOrder(notes: any) {
  return async (dispatch: Dispatch<AppState>, getStore: () => AppState) => {
    dispatch(saveServiceWorkOrderStart());
    try {
      const storeState = getStore();
      if (storeState.serviceWorkOrder.serviceWorkOrderVM) {
        let swoVM = {
          ...storeState.serviceWorkOrder.serviceWorkOrderVM,
          internalNotes: notes.internalNotes,
          commentToVendor: notes.vendorNotes
        };
        const savedSWO = await swoService.saveSWO(swoVM, storeState.serviceWorkOrder.linkTicketId);
        dispatch(setSwoTicketId(undefined));
        actions.change('serviceWorkOrderForm', {});
        return dispatch(saveServiceWorkOrderSuccess(savedSWO));
      }

      return Promise.reject('');
    } catch (err) {
      actions.change('serviceWorkOrderForm', {});
      return dispatch(saveServiceWorkOrderFailure(err));
    }
  };
}

export function saveServiceWorkOrderStart(): SaveServiceWorkOrderStartAction {
  return {
    type: ServiceWorkOrderActionType.SaveServiceWorkOrderStart
  };
}
export function saveServiceWorkOrderSuccess(savedSWO: ServiceWorkOrderViewModel): SaveServiceWorkOrderSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={'Successfully saved Service Work Order # ' + savedSWO.id} />,
    ToastOptions.Success
  );

  return {
    type: ServiceWorkOrderActionType.SaveServiceWorkOrderSuccess,
    id: savedSWO.id,
    swoVM: savedSWO
  };
}
export function saveServiceWorkOrderFailure(errorMessage: string): SaveServiceWorkOrderFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Failed to save Service Work Order. Error: ' + errorMessage} />,
    ToastOptions.Error
  );
  return {
    type: ServiceWorkOrderActionType.SaveServiceWorkOrderFailure,
    errorMessage: errorMessage
  };
}

/************* Search SWO *************/
export function searchServiceWorkOrder(
  query: string | null,
  pageSize: number,
  pageNumber: number,
  orderBy: string,
  orderByDirection: string,
  showInactive?: boolean,
  contractId?: number
) {
  return async (dispatch: Dispatch<ServiceWorkOrderSearchViewModel>) => {
    dispatch(searchServiceWorkOrderStart(query, showInactive));
    try {
      let result: PageViewModel<ServiceWorkOrderSearchViewModel> = await swoService.search(
        pageNumber,
        pageSize,
        orderBy,
        orderByDirection,
        query ? query : undefined,
        contractId,
        showInactive ? false : true
      );
      dispatch(searchServiceWorkOrderSuccess(pageNumber, pageSize, orderBy, result!));
    } catch (e) {
      dispatch(searchServiceWorkOrderFailure(e.toString()));
    }
  };
}

export function resetSWOSearch(): ResetSWOSearchAction {
  return {
    type: ServiceWorkOrderActionType.ResetSWOSearch
  };
}

export function searchServiceWorkOrderStart(
  query: string | null,
  showInactive: boolean | undefined
): SearchServiceWorkOrderStartAction {
  return {
    type: ServiceWorkOrderActionType.SearchServiceWorkOrderStart,
    query: query,
    showInactive: showInactive
  };
}
export function searchServiceWorkOrderSuccess(
  pageNumber: number,
  pageSize: number,
  orderBy: string,
  result: PageViewModel<ServiceWorkOrderSearchViewModel>
): SearchServiceWorkOrderSuccessAction {
  return {
    pageNumber: pageNumber,
    pageSize: pageSize,
    orderBy: orderBy,
    result: result,
    type: ServiceWorkOrderActionType.SearchServiceWorkOrderSuccess
  };
}
export function searchServiceWorkOrderFailure(error: string): SearchServiceWorkOrderFailureAction {
  return {
    type: ServiceWorkOrderActionType.SearchServiceWorkOrderFailure,
    error: error
  };
}

/********** GetServiceWorkOrderContract **********/
export function getServiceWorkOrderContract(contractId: number) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getServiceWorkOrderContractStart());
    try {
      const swoContract = await swoService.getContract(contractId);
      swoContract.contractId = contractId;
      var customer = {} as CustomerViewModel;
      if (swoContract) {
        if (swoContract.endDate && swoContract.startDate) {
          if (!moment.isMoment(swoContract.endDate)) {
            swoContract.endMoment = dateParser.parse(swoContract.endDate);
          } else {
            // already a moment
            swoContract.endMoment = swoContract.endDate;
          }
          if (swoContract.startDate && !moment.isMoment(swoContract.startDate)) {
            swoContract.startMoment = dateParser.parse(swoContract.startDate);
          } else {
            // already a moment
            swoContract.startMoment = swoContract.startDate;
          }
          let duration = moment.duration(swoContract.endMoment.diff(swoContract.startMoment));
          swoContract.remainingDays = duration.days();
        }
        if (swoContract.orderDate) {
          if (!moment.isMoment(swoContract.orderDate)) {
            swoContract.orderMoment = dateParser.parse(swoContract.orderDate);
          } else {
            // already a moment
            swoContract.orderMoment = swoContract.orderDate;
          }
        }
        if (swoContract.customerId > 0) {
          customer = await customerService.getCustomer(swoContract.customerId);
        }
        dispatch(actions.change('serviceWorkOrderForm.contractId', swoContract.contractId));
      }
      dispatch(getServiceWorkOrderContractSuccess(swoContract, customer));
    } catch (err) {
      dispatch(getServiceWorkOrderContractFailure(err));
    }
  };
}
export function getServiceWorkOrderContractStart(): GetServiceWorkOrderContractStartAction {
  return {
    type: ServiceWorkOrderActionType.GetServiceWorkOrderContractStart,
    message: 'Getting Service Work Order Contract'
  };
}
export function getServiceWorkOrderContractSuccess(
  swoContract: ServiceWorkOrderViewModel,
  customer?: CustomerViewModel
): GetServiceWorkOrderContractSuccessAction {
  return {
    type: ServiceWorkOrderActionType.GetServiceWorkOrderContractSuccess,
    message: 'Get Service Work Order Contract Success',
    swoContract: swoContract,
    customer: customer
  };
}
export function getServiceWorkOrderContractFailure(errorMessage: string): GetServiceWorkOrderContractFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Failed to get Service Work Order Contract. Error: ' + errorMessage}
    />,
    ToastOptions.Error
  );
  return {
    type: ServiceWorkOrderActionType.GetServiceWorkOrderContractFailure,
    errorMessage: errorMessage
  };
}

/**** Step 3: Service Fee Payment ******/
export function saveServiceWorkOrderPaymentInfo(
  id: number,
  payment: SwoPaymentViewModel,
  callback: () => void,
  firstBilling?: OptionsViewModel,
  laterBills?: OptionsViewModel
) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(saveServiceFeePaymentInfoStart());
    try {
      await swoService.saveSwoPayment(id, payment);
      dispatch(saveServiceFeePaymentInfoSuccess(id, firstBilling, laterBills));
      callback();
    } catch (e) {
      dispatch(saveServiceFeePaymentInfoFailure(e.message));
    }
  };
}
export function saveServiceFeePaymentInfoStart(): SaveServiceFeePaymentInfoStartAction {
  return {
    type: ServiceWorkOrderActionType.SaveServiceFeePaymentInfoStart,
    message: 'Saving Payment Info Start'
  };
}
export function saveServiceFeePaymentInfoSuccess(
  serviceWorkOrderId: number,
  firstBilling?: OptionsViewModel,
  laterBills?: OptionsViewModel
): SaveServiceFeePaymentInfoSuccessAction {
  toast.success(
    <ToastComponent
      type={ToastType.Success}
      message={`Service Work Order: ${serviceWorkOrderId} Updated and Finalized`}
    />,
    ToastOptions.Success
  );

  return {
    type: ServiceWorkOrderActionType.SaveServiceFeePaymentInfoSuccess,
    serviceWorkOrderId: serviceWorkOrderId,
    message: 'Service Work Order: ' + serviceWorkOrderId + ' Updated and Finalized'
  };
}
export function saveServiceFeePaymentInfoFailure(errorMessage: string): SaveServiceFeePaymentInfoFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Service Work Order could not be updated/finalized. Error: ' + errorMessage}
    />,
    ToastOptions.Error
  );
  return {
    type: ServiceWorkOrderActionType.SaveServiceFeePaymentInfoFailure,
    errorMessage: 'Service Work Order could not be updated. Error: ' + errorMessage
  };
}

/***** Step 4: Get/Add/Remove New Inventory ******/
export function getInventoryList(id: number) {
  return async (dispatch: Dispatch<ContractCviViewModel>) => {
    dispatch(getInventoryListStart());
    try {
      let result: Array<ContractCviViewModel> = await contractService.getContractCVI(id); // await propertyManagementService.searchPropertyManagers(query, null, pageSize, pageNumber, orderBy);
      dispatch(getInventoryListSuccess(result));
    } catch (e) {
      dispatch(getInventoryListFailure(e.toString()));
    }
  };
}
export function getInventoryListStart(): GetInventoryListStartAction {
  return {
    type: ServiceWorkOrderActionType.GetInventoryListStart
  };
}
export function getInventoryListSuccess(inventoryList: Array<ContractCviViewModel>): GetInventoryListSuccessAction {
  return {
    type: ServiceWorkOrderActionType.GetInventoryListSuccess,
    inventoryList: inventoryList
  };
}
export function getInventoryListFailure(error: string): GetInventoryListFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Failed to get Inventory List: ' + error} />,
    ToastOptions.Error
  );

  return {
    type: ServiceWorkOrderActionType.GetInventoryListFailure,
    message: error
  };
}

export function addNewInventory(formData: ContractCviViewModel): AddNewInventoryAction {
  return {
    type: ServiceWorkOrderActionType.AddNewInventory,
    inventoryEntry: formData
  };
}

export function addNewExclusion(formData: CVIExclusionViewModel) {
  return async (dispatch: Dispatch<CVIExclusionViewModel>) => {
    dispatch(addNewExclusionStart());
    try {
      await contractService.saveExclusion(formData); // await propertyManagementService.searchPropertyManagers(query, null, pageSize, pageNumber, orderBy);
      dispatch(getExclusionList(formData.contractId));
      dispatch(getInventoryList(formData.contractId));
      dispatch(addNewExclusionSuccess(formData));
    } catch (e) {
      dispatch(addNewExclusionFailure(e.toString()));
    }
  };
}
export function addNewExclusionStart(): AddNewExclusionStartAction {
  return {
    type: ServiceWorkOrderActionType.AddNewExclusionStart
  };
}
export function addNewExclusionSuccess(exclusionEntry: CVIExclusionViewModel): AddNewExclusionSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={'Successfully Excluded Item!'} />,
    ToastOptions.Success
  );
  return {
    type: ServiceWorkOrderActionType.AddNewExclusionSuccess,
    exclusionEntry: exclusionEntry
  };
}
export function addNewExclusionFailure(error: string): AddNewExclusionFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Failed to add exclusion! ' + error} />,
    ToastOptions.Error
  );
  return {
    type: ServiceWorkOrderActionType.AddNewExclusionFailure,
    message: error
  };
}

/******* Get Exclusion List *******/
export function getExclusionList(id: number) {
  return async (dispatch: Dispatch<ContractExclusionViewModel>) => {
    dispatch(getExclusionListStart());
    try {
      await contractService.getExclusionList(id).then(result => {
        dispatch(getExclusionListSuccess(result));
      });
    } catch (e) {
      dispatch(getExclusionListFailure(e));
    }
  };
}
export function getExclusionListStart(): GetExclusionListStartAction {
  return {
    type: ServiceWorkOrderActionType.GetExclusionListStart
  };
}
export function getExclusionListSuccess(
  exclusionList: Array<ContractExclusionViewModel>
): GetExclusionListSuccessAction {
  return {
    type: ServiceWorkOrderActionType.GetExclusionListSuccess,
    exclusionList: exclusionList
  };
}
export function getExclusionListFailure(error: string): GetExclusionListFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Failed to get Exclusion List: ' + error} />,
    ToastOptions.Error
  );
  return {
    type: ServiceWorkOrderActionType.GetExclusionListFailure,
    message: error
  };
}

/***** Get Exclusion Types ******/

export function getExclusionTypes() {
  return async (dispatch: Dispatch<OptionsViewModel>) => {
    dispatch(getExclusionTypesStart());
    try {
      let result = await swoService.getExclusionTypes();
      dispatch(getExclusionTypesSuccess(result));
    } catch (e) {
      dispatch(getExclusionTypesFailure(e));
    }
  };
}

export function getExclusionTypesStart(): GetExclusionTypesStartAction {
  return {
    type: ServiceWorkOrderActionType.GetExclusionTypesStart
  };
}
export function getExclusionTypesSuccess(exclusionTypes: Array<OptionsViewModel>): GetExclusionTypesSuccessAction {
  return {
    type: ServiceWorkOrderActionType.GetExclusionTypesSuccess,
    exclusionTypes: exclusionTypes
  };
}
export function getExclusionTypesFailure(error: string): GetExclusionTypesFailureAction {
  return {
    type: ServiceWorkOrderActionType.GetExclusionTypesFailure,
    message: error
  };
}

/********** SelectCVI **********/
export function selectCVI(cvi: ContractCviViewModel): SelectCVIAction {
  return {
    type: ServiceWorkOrderActionType.SelectCVI,
    cvi: cvi
  };
}

export function addSelectedCVI(cvi: number): AddSelectedCVIAction {
  return {
    type: ServiceWorkOrderActionType.AddSelectedCVI,
    cvi: cvi
  };
}

/********** GetServiceWorkOrderContractCVI **********/
export function getServiceWorkOrderContractCVI(contractId: number) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getServiceWorkOrderContractCVIStart());
    try {
      const swoContractCVIs = await contractService.getContractCVI(contractId);
      dispatch(getServiceWorkOrderContractCVISuccess(swoContractCVIs));
    } catch (err) {
      dispatch(getServiceWorkOrderContractCVIFailure(err));
    }
  };
}
export function getServiceWorkOrderContractCVIStart(): GetServiceWorkOrderContractCVIStartAction {
  return {
    type: ServiceWorkOrderActionType.GetServiceWorkOrderContractCVIStart,
    message: 'Getting Service Work Order Contract CVIs'
  };
}
export function getServiceWorkOrderContractCVISuccess(
  swoContractCVIs: ContractCviViewModel[]
): GetServiceWorkOrderContractCVISuccessAction {
  return {
    type: ServiceWorkOrderActionType.GetServiceWorkOrderContractCVISuccess,
    message: 'Get Service Work Order Contract CVIs Success',
    swoContractCVIs: swoContractCVIs
  };
}
export function getServiceWorkOrderContractCVIFailure(
  errorMessage: string
): GetServiceWorkOrderContractCVIFailureAction {
  toast.error(
    <ToastComponent
      type={ToastType.Error}
      message={'Failed to get Service Work Order Contract CVIs. Error: ' + errorMessage}
    />,
    ToastOptions.Error
  );
  return {
    type: ServiceWorkOrderActionType.GetServiceWorkOrderContractCVIFailure,
    errorMessage: errorMessage
  };
}

export function saveStep1FormToSWOCreate() {
  return async (dispatch: AppDispatcher, getState: () => AppState): Promise<any> => {
    const swoStep1FormState: ServiceWorkOrderViewModel = getState().serviceWorkOrderForm;
    return dispatch(saveServiceWorkOrderCreate(swoStep1FormState));
  };
}

/********** SaveServiceWorkOrderCreate **********/
export function saveServiceWorkOrderCreate(swo: ServiceWorkOrderViewModel): SaveServiceWorkOrderCreateAction {
  return {
    type: ServiceWorkOrderActionType.SaveServiceWorkOrderCreate,
    swo: swo
  };
}

/***** Get Contract Cost History *****/

export function getContractCostHistory(id: number) {
  return async (dispatch: Dispatch<ContractCostHistoryViewModel[]>) => {
    dispatch(getContractCostHistoryStart());
    try {
      let result = await contractService.getCostHistory(id);
      dispatch(getContractCostHistorySuccess(result));
    } catch (e) {
      dispatch(getContractCostHistoryFailure(e));
    }
  };
}

export function getContractCostHistoryStart(): GetContractCostHistoryStartAction {
  return {
    type: ServiceWorkOrderActionType.GetContractCostHistoryStart
  };
}
export function getContractCostHistorySuccess(
  costHistory: ContractTotalCostHistoryViewModel[]
): GetContractCostHistorySuccessAction {
  return {
    type: ServiceWorkOrderActionType.GetContractCostHistorySuccess,
    costHistory: costHistory
  };
}
export function getContractCostHistoryFailure(error: string): GetContractCostHistoryFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Failed to get Cost History: ' + error} />,
    ToastOptions.Error
  );
  return {
    type: ServiceWorkOrderActionType.GetContractCostHistoryFailure,
    message: error
  };
}

/**** Edit Exclusion ****/

export function editExclusion(exclusionEntry: ContractExclusionViewModel) {
  return async (dispatch: Dispatch<ContractExclusionViewModel>) => {
    dispatch(editExclusionStart());
    try {
      await contractService.saveExclusion(exclusionEntry);
      dispatch(getInventoryList(exclusionEntry.contractId));
      dispatch(getExclusionList(exclusionEntry.contractId));
      dispatch(editExclusionSuccess());
    } catch (e) {
      dispatch(editExclusionFailure(e));
    }
  };
}

export function editExclusionStart(): EditExclusionStartAction {
  return {
    type: ServiceWorkOrderActionType.EditExclusionStart
  };
}
export function editExclusionSuccess(): EditExclusionSuccessAction {
  return {
    type: ServiceWorkOrderActionType.EditExclusionSuccess
  };
}
export function editExclusionFailure(error: string): EditExclusionFailureAction {
  return {
    type: ServiceWorkOrderActionType.EditExclusionFailure,
    message: error
  };
}

/*** Edit/Load SWO ***/

export function loadSWO(swo: ServiceWorkOrderViewModel) {
  return (dispatch: Dispatch<AppState>) => {
    dispatch(actions.load('serviceWorkOrderForm', { ...swo }));
    dispatch(loadSWOIntoState(swo));
  };
}

export function loadSWOIntoState(swo: ServiceWorkOrderViewModel): LoadSWOAction {
  return {
    type: ServiceWorkOrderActionType.LoadSWO,
    swo: swo
  };
}

/**** Assign Vendor To SWO ******/
export function storeVendor(swoId: number, vendorInfo: { id: number; vendor: string }): StoreVendorAction {
  return {
    vendorId: vendorInfo.id,
    type: ServiceWorkOrderActionType.StoreVendor
  };
}

export function assignVendorOnHold(swoId: number, vendorInfo: { id: number; vendor: string }) {
  return async (dispatch: Dispatch<AppState>, getStore: () => AppState) => {
    dispatch(assignVendorStart());

    await swoService
      .assignVendorOnHold(swoId, vendorInfo)
      .then(() => {
        dispatch(assignVendorSuccess(swoId));
      })
      .then(async () => {
        return await dispatch(getSwo(swoId));
      })
      .catch(err => dispatch(assignVendorFailure(err.toString())));
  };
}

export function assignVendor(swoId: number, vendorInfo: { id: number; vendor: string }, isPayment?: boolean) {
  return async (dispatch: Dispatch<AppState>, getStore: () => AppState) => {
    dispatch(assignVendorStart());

    await swoService
      .assignVendor(swoId, vendorInfo)
      .then(() => {
        dispatch(assignVendorSuccess(swoId, true));
      })
      .then(() => {
        dispatch(getSwo(swoId));
      })
      .catch(err => dispatch(assignVendorFailure(err.toString())));
  };
}

export function emailSwoVendor(swoId: number) {
  return async (dispatch: Dispatch<AppState>) => {
    await swoService
      .resendVendorEmail(swoId)
      .then(() => {
        dispatch(resendEmailSuccess(swoId));
      })
      .catch(err => {
        dispatch(resendEmailFailure(err.toString()));
      });
  };
}
export function resendEmailSuccess(swoId: number): ResendEmailSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={`Vendor email for SWO #${swoId} has been resent`} />,
    ToastOptions.Success
  );

  return {
    type: ServiceWorkOrderActionType.AssignVendorSuccess,
    message: 'Success!!'
  };
}

export function resendEmailFailure(e: string): ResendEmailFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={e} />, ToastOptions.Error);

  return {
    message: e,
    type: ServiceWorkOrderActionType.AssignVendorFailure
  };
}
export function assignVendorAfterPayment(swoId: number, vendorId?: number) {
  return (dispatch: Dispatch<AppState>, getStore: () => AppState) => {
    if (vendorId) {
      dispatch(getVendorDetails(vendorId)).then(async () => {
        const store = await getStore();
        dispatch(assignVendor(swoId, { id: store.vendor.vendorVM.id, vendor: store.vendor.vendorVM.companyName }));
      });
    }
  };
}

export function assignVendorStart(): AssignVendorStartAction {
  return {
    type: ServiceWorkOrderActionType.AssignVendorStart
  };
}

export function assignVendorSuccess(swoId: number, isPayment?: boolean): AssignVendorSuccessAction {
  if (isPayment) {
    toast.success(
      <ToastComponent type={ToastType.Success} message={`SWO #${swoId} has been Dispatched`} />,
      ToastOptions.Success
    );
  } else {
    toast.success(
      <ToastComponent
        type={ToastType.Success}
        message={`SWO #${swoId} Saved Successfully and will Dispatch on Payment`}
      />,
      ToastOptions.Success
    );
  }

  return {
    type: ServiceWorkOrderActionType.AssignVendorSuccess,
    message: 'Success!!'
  };
}

export function assignVendorFailure(e: string): AssignVendorFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={e} />, ToastOptions.Error);

  return {
    message: e,
    type: ServiceWorkOrderActionType.AssignVendorFailure
  };
}

export function resetSaveAndClose(): ResetSaveAndCloseAction {
  return {
    type: ServiceWorkOrderActionType.ResetSaveAndClose
  };
}

/****** Get Trip Status Log ******/

export function getTripStatusLog(id: number) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getTripStatusLogStart());
    try {
      let result = await swoService.getTripStatusLog(id);
      dispatch(getTripStatusLogSuccess(result));
    } catch (e) {
      dispatch(getTripStatusLogFailure(e));
    }
  };
}

export function getTripStatusLogStart(): GetTripStatusLogStartAction {
  return {
    type: ServiceWorkOrderActionType.GetTripStatusLogStart
  };
}
export function getTripStatusLogSuccess(result: Array<TripStatusLogViewModel>): GetTripStatusLogSuccessAction {
  return {
    type: ServiceWorkOrderActionType.GetTripStatusLogSuccess,
    statusLog: result
  };
}
export function getTripStatusLogFailure(error: string): GetTripStatusLogFailureAction {
  return {
    type: ServiceWorkOrderActionType.GetTripStatusLogFailure,
    message: error
  };
}

/***** Redispatch SWO *****/
export function redispatchSWOFailure(e: string): RedispatchSWOFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={e} />, ToastOptions.Error);
  return {
    type: ServiceWorkOrderActionType.RedispatchSWOFailure
  };
}

export function redispatchSWOStart(): RedispatchSWOStartAction {
  return {
    type: ServiceWorkOrderActionType.RedispatchSWOStart
  };
}

export function redispatchSWOSuccess(): RedispatchSWOSuccessAction {
  toast.success(<ToastComponent type={ToastType.Success} message={'Vendor Assigned to SWO'} />, ToastOptions.Success);
  return {
    type: ServiceWorkOrderActionType.RedispatchSWOSuccess
  };
}

export function redispatchSWO(
  swoId: number,
  vendorInfo: { id: number; vendor: string },
  redispatchTypeId: number,
  redispatchComment: string
) {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(redispatchSWOStart());
    try {
      await swoService.redispatchSWO(swoId, vendorInfo, redispatchTypeId, redispatchComment).then(() => {
        dispatch(redispatchSWOSuccess());
        dispatch(getSwo(swoId));
        dispatch(getVendorDetails(vendorInfo.id));
      });
    } catch (e) {
      dispatch(redispatchSWOFailure(e.toString()));
    }
  };
}

export interface SetTypesAction extends Action {
  readonly type: ServiceWorkOrderActionType.SetTypes;
  readonly swoTypes: OptionsViewModel[];
}
export function setTypes(types: OptionsViewModel[]): SetTypesAction {
  return {
    type: ServiceWorkOrderActionType.SetTypes,
    swoTypes: types
  };
}

export interface SetTypeIdAction extends ServiceWorkOrderAction {
  readonly type: ServiceWorkOrderActionType.SetTypeId;
  readonly typeId: number;
}
export function setTypeId(typeId: number): SetTypeIdAction {
  return {
    type: ServiceWorkOrderActionType.SetTypeId,
    typeId: typeId
  };
}

export interface SetSecondOpinionSWOAction extends ServiceWorkOrderAction {
  readonly type: ServiceWorkOrderActionType.SetSecondOpinionSWO;
  readonly swoTypeId: number;
  readonly parentSWOId: number;
  readonly parentVendorId?: number;
  readonly comment?: string;
}
export function setParentSWO(
  typeId: number,
  parentId: number,
  parentVendorId?: number,
  comment?: string
): SetSecondOpinionSWOAction {
  return {
    type: ServiceWorkOrderActionType.SetSecondOpinionSWO,
    swoTypeId: typeId,
    parentSWOId: parentId,
    parentVendorId: parentVendorId,
    comment: comment
  };
}
export function setSecondOpinionServiceWorkOrder(comment?: string) {
  return async (dispatch: AppDispatcher, getState: () => AppState) => {
    let secondOpinionTypeId = ServiceWorkOrderTypes.SecondOpinion;
    let parentSWO = getState().serviceWorkOrder.serviceWorkOrderVM;
    if (parentSWO) {
      await dispatch(setParentSWO(secondOpinionTypeId, parentSWO.id, parentSWO.vendorId, comment));
      history.push(getRoute(RouteDefinitions.contract.Swo, { id: parentSWO.contractId }));
      return Promise.resolve();
    }
    return Promise.reject('');
  };
}



// WEBPACK FOOTER //
// ./src/service-work-order/service-work-order.actions.tsx